import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CoreTranslationService } from '@core/services/translation.service';

import { locale as english } from '../i18n/en';
import { locale as french } from '../i18n/fr';
import { locale as german } from '../i18n/de';
import { locale as portuguese } from '../i18n/pt';
import { StatisticsService } from 'app/services/statistics/statistics.service';
import { IBasicStats } from 'app/interfaces/statistics.interface';
import { IUser } from 'app/interfaces/user.interface';
import { ActiveUser } from 'app/utils/helpers.utils';
import { FeeService } from 'app/services/fee/fee.service';
import { IFeeStatistics } from 'app/interfaces/fee.interface';
import { IAdminDashboardStats } from 'app/interfaces/dashboard.interface';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardComponent implements OnInit {

  // Public
  public data: any;
  public currentUser: IUser;
  public isAdmin: boolean;
  public isClient: boolean;
  public isLoading = true;
  public today = new Date();
  public todayDate = new Date().getDate();
  public currentLanguage = 'en';

  // Legacy stats (kept for backwards compatibility)
  basicStats: IBasicStats;
  colors = [];
  user: IUser = ActiveUser();
  feeStats: any = {
    totalCollected: 0,
    totalRemaining: 0,
    totalDue: 0,
    pendingAmount: 0,
    recentPayments: []
  };
  feeStatistics: IFeeStatistics;

  // New Admin Dashboard Stats
  dashboardStats: IAdminDashboardStats;

  // Financial period filter
  financialPeriod: string = 'month';

  constructor(
    private _coreTranslationService: CoreTranslationService,
    private translateService: TranslateService,
    private statsService: StatisticsService,
    private feeService: FeeService) {
    this.isAdmin = this.user.role.name.toUpperCase() === 'ADMIN';
    this.isClient = this.user.role.name.toUpperCase() === 'CLIENT';
    this._coreTranslationService.translate(english, french, german, portuguese);
  }

  ngOnInit(): void {
    this.currentLanguage = this.translateService.currentLang || this.translateService.defaultLang || 'en';

    this.translateService.onLangChange.subscribe((event) => {
      this.currentLanguage = event.lang;
    });

    this.loadDashboardData();
  }

  getFormattedDate(): string {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    const locale = this.currentLanguage === 'fr' ? 'fr-FR' : 'en-US';
    return this.today.toLocaleDateString(locale, options);
  }

  getFormattedMonth(): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };

    const locale = this.currentLanguage === 'fr' ? 'fr-FR' : 'en-US';
    return this.today.toLocaleDateString(locale, options);
  }

  getFormattedTime(): string {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit'
    };

    const locale = this.currentLanguage === 'fr' ? 'fr-FR' : 'en-US';
    return this.today.toLocaleTimeString(locale, options);
  }

  getFormattedTransactionDate(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };

    const locale = this.currentLanguage === 'fr' ? 'fr-FR' : 'en-US';
    return dateObj.toLocaleDateString(locale, options);
  }

  loadDashboardData(): void {
    this.isLoading = true;

    // Load new admin dashboard stats
    this.statsService.getAdminDashboardStats().subscribe(
      response => {
        this.dashboardStats = response.data;

        // Also load legacy stats for backwards compatibility
        this.loadLegacyStats();
      },
      error => {
        console.error('Error loading dashboard statistics:', error);
        // Fallback to legacy stats
        this.loadLegacyStats();
      }
    );
  }

  loadLegacyStats(): void {
    this.statsService.getAllStatistics().subscribe(
      response => {
        this.basicStats = response.data;
        this.colors = this.generateColors(this.basicStats?.classStats?.length || 0);
        this.loadFeeStatistics();
      },
      error => {
        console.error('Error loading statistics:', error);
        this.isLoading = false;
      }
    );
  }

  refreshDashboard(): void {
    this.loadDashboardData();
  }

  generateColors(l: number) {
    const colorPalette = [
      '#3B82F6', // Primary blue
      '#6366F1', // Indigo
      '#8B5CF6', // Violet
      '#EC4899', // Pink
      '#F43F5E', // Rose
      '#EF4444', // Red
      '#F59E0B', // Amber
      '#10B981', // Emerald
      '#06B6D4', // Cyan
      '#3B82F6', // Primary blue (repeat for consistency)
    ];

    if (l <= colorPalette.length) {
      return colorPalette.slice(0, l);
    } else {
      let colors = [...colorPalette];
      let n = colorPalette.length;
      while (n < l) {
        let color = Math.floor(Math.random() * 16777215).toString(16);
        color = "#" + color;
        colors.push(color);
        n++;
      }
      return colors;
    }
  }

  getActiveSequence() {
    return this.basicStats?.semester?.sequence.find(seq => seq.is_current);
  }

  loadFeeStatistics() {
    this.feeService.getFeeStatistics().subscribe(
      response => {
        this.feeStatistics = response.data;
        this.isLoading = false;
      },
      error => {
        console.error('Error loading fee statistics:', error);
        this.isLoading = false;
      }
    );
  }

  // New dashboard stats helper methods
  getCollectionRate(): number {
    if (!this.dashboardStats?.financials) return 0;
    return this.dashboardStats.financials.collectionRate;
  }

  getOutstandingPercentage(): number {
    if (!this.dashboardStats?.financials) return 0;
    const total = this.dashboardStats.financials.totalFeesExpected;
    if (total === 0) return 0;
    return (this.dashboardStats.financials.totalFeesOutstanding / total) * 100;
  }

  getTrendIcon(change: number): string {
    if (change > 0) return 'pi pi-arrow-up';
    if (change < 0) return 'pi pi-arrow-down';
    return 'pi pi-minus';
  }

  getTrendClass(change: number): string {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  }

  getPerformanceClass(score: number): string {
    if (score >= 16) return 'excellent';
    if (score >= 14) return 'good';
    if (score >= 10) return 'average';
    return 'poor';
  }

  getAlertIcon(type: string): string {
    switch (type) {
      case 'warning': return 'pi pi-exclamation-triangle';
      case 'error': return 'pi pi-times-circle';
      case 'info': return 'pi pi-info-circle';
      default: return 'pi pi-bell';
    }
  }

  getAlertClass(severity: string): string {
    switch (severity) {
      case 'high': return 'alert-danger';
      case 'medium': return 'alert-warning';
      case 'low': return 'alert-info';
      default: return 'alert-info';
    }
  }

  // Legacy helper methods (kept for backwards compatibility)
  getFeePercentage(type: 'collected' | 'due' | 'remaining'): number {
    if (!this.feeStatistics) return 0;

    const totalCollected = this.feeStatistics.total_collected || 0;
    const totalExpected = this.feeStatistics.total_expected || 0;
    const totalOutstanding = this.feeStatistics.total_outstanding || 0;

    const total = totalExpected || (totalCollected + totalOutstanding);

    if (total === 0) return 0;

    switch (type) {
      case 'collected':
        return (totalCollected / total) * 100;
      case 'due':
        return (totalOutstanding / total) * 100;
      case 'remaining':
        return (totalOutstanding / total) * 100;
      default:
        return 0;
    }
  }

  getClassPercentage(count: number): number {
    if (!this.basicStats?.classStats || this.basicStats.classStats.length === 0) return 0;

    const maxCount = Math.max(...this.basicStats.classStats.map(stat => stat.learners?.length || 0));

    if (maxCount === 0) return 0;

    return (count / maxCount) * 100;
  }

  getAverageClassSize(): number {
    if (!this.basicStats?.classStats || this.basicStats.classStats.length === 0) return 0;

    const totalStudents = this.basicStats.classStats.reduce((sum, stat) => sum + (stat.learners?.length || 0), 0);
    return totalStudents / this.basicStats.classStats.length;
  }

  getLargestClass(): any {
    if (!this.basicStats?.classStats || this.basicStats.classStats.length === 0) return null;

    return this.basicStats.classStats.reduce((max, current) => {
      return (current.learners?.length || 0) > (max.learners?.length || 0) ? current : max;
    }, this.basicStats.classStats[0]);
  }

  getStatusClass(status: string): string {
    if (!status) return '';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'status-success';
      case 'pending':
        return 'status-warning';
      case 'failed':
      case 'overdue':
        return 'status-danger';
      default:
        return 'status-info';
    }
  }

  getPaymentMethodIcon(method: string): string {
    switch (method) {
      case 'Cash': return 'pi pi-money-bill';
      case 'Bank Transfer': return 'pi pi-building';
      case 'Credit Card': return 'pi pi-credit-card';
      case 'Mobile Money': return 'pi pi-mobile';
      default: return 'pi pi-wallet';
    }
  }

  formatCurrency(amount: number): string {
    if (!amount) return '0';
    return new Intl.NumberFormat(this.currentLanguage === 'fr' ? 'fr-FR' : 'en-US').format(amount);
  }

  getRevenueBarHeight(amount: number): number {
    if (!amount) return 10;
    const maxAmount = 60000000; // Max expected monthly revenue
    return Math.max(10, (amount / maxAmount) * 100);
  }

  getPerformanceBarClass(index: number): string {
    const colors = ['green', 'blue', 'orange'];
    return colors[index] || 'blue';
  }
}
